/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Snackbar,
  Backdrop,
  CircularProgress,
  Breadcrumbs,
  Link,
  Container,
  Switch
} from "@mui/material";
import PropTypes from "prop-types";

// LoadingBackdrop Component - Reusable loading indicator
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
    open={open}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool.isRequired,
};

import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import "./EditOneTimeProduct.css";
import Topheader from "../../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import ApplicationDetailsErrorDialog from "../../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { DataGrid } from "@mui/x-data-grid";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

// Constants moved outside component to avoid recreation on every render
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "March",
  "Apr",
  "May",
  "June",
  "July",
  "August",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

const FREQUENCY_UNIT_CONVERTER = {
  "1-DAY": "Daily",
  "1-MONTH": "Monthly",
  "3-MONTH": "Quarterly",
  "6-MONTH": "Half yearly",
  "1-YEAR": "Yearly",
};

// Data grid configuration - moved outside component
const DATA_GRID_CONFIG = {
  rowHeight: 34,
  columnHeaderHeight: 32,
  pageSizeOptions: [5, 10, 25],
  initialState: {
    pagination: {
      paginationModel: { page: 0, pageSize: 5 },
    },
  },
  sx: {
    '& .MuiDataGrid-cell': {
      padding: '4px 8px',
      display: 'flex',
      alignItems: 'center',
      height: '34px',
      minHeight: '34px',
    },
    '& .MuiDataGrid-columnHeader': {
      padding: '4px 8px',
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      minHeight: '32px',
    },
    '& .country-header-first': {
      paddingLeft: '16px !important'
    },
    '& .MuiDataGrid-columnHeader.tax-rate-header': {
      paddingLeft: '24px !important'
    },
    '& .MuiDataGrid-row': {
      minHeight: '34px !important',
      maxHeight: '34px !important',
    },
    '& .MuiDataGrid-cellContent': {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    },
  },
};

// Country table columns configuration - moved outside component
const COUNTRY_TABLE_COLUMNS = [
  {
    field: "country",
    headerName: "Country",
    width: 140,
    headerClassName: "country-table-general-header country-header-first",
    renderCell: (params) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '8px' }}>
        {params.value}
      </div>
    ),
  },
  {
    field: "currencyCode",
    headerName: "Price",
    align: "right",
    headerAlign: "right",
    sortable: false,
    headerClassName: "country-table-general-header",
    renderCell: (params) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
        <span style={{color: "#717273"}}>
          {params.value}
        </span>
      </div>
    ),
  },
  {
    field: "price",
    headerName: "",
    editable: false,
    align: "left",
    headerAlign: "left",
    type: "number",
    width: 80,
    headerClassName: "price-colum-header",
    renderCell: (params) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params.value}
      </div>
    ),
  },
  {
    field: "taxRate",
    headerName: "Tax Rate",
    width: 140,
    align: "left",
    headerAlign: "left",
    headerClassName: "country-table-general-header tax-rate-header",
    renderCell: (params) => {
      const parts = params.value.split(' ');
      if (parts.length > 0) {
        parts[0] = parts[0] + '%';
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-start', paddingLeft: '16px' }}>
          {parts.join(' ')}
        </div>
      );
    },
  },
];

// Helper function to build price data with tax - moved outside component
const buildPriceDataTax = (json) => {
  const priceData = json.oneTimePlan.planPrices.map(pData => ({
    country: pData.country,
    id: pData.id,
    currencyCode: pData.currencyCode,
    price: pData.price,
    taxRateinPercent: pData.taxRateinPercent,
    countryCode: pData.countryCode,
    taxRate: pData.taxRate,
  }));
  priceData.push({
    country: json.oneTimePlan.basePrice.country,
    id: json.oneTimePlan.basePrice.id,
    currencyCode: json.oneTimePlan.basePrice.currencyCode,
    price: json.oneTimePlan.basePrice.price,
    taxRateinPercent: json.oneTimePlan.basePrice.taxRateinPercent,
    countryCode: json.oneTimePlan.basePrice.countryCode,
    taxRate: json.oneTimePlan.basePrice.taxRate,
  });
  return priceData;
};

// Helper function to create country data grid rows
const createCountryDataRows = (data) => {
  return data.map((country, index) => ({
    id: index + 1,
    country: country.country,
    currencyCode: country.currencyCode,
    price: country.price,
    taxRate:
      country.taxRateinPercent +
      " (" +
      country.currencyCode +
      " " +
      country.taxRate +
      ")",
  }));
};

function EditOneTimeProduct(props) {
  const { appId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const teamId = location.state.teamId;
  const sellerId = location.state.sellerId;
  const [packageName, setPackageName] = useState("");
  const [exchangePriceData, setExchangePriceData] = useState([]);
  const [product, setProduct] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const { loading, submissionDetails } = useSelector(
    (state) => state.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "onChange",
  });

  const errorsApp = useSelector((state) => state.errors);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [countryTableRows, setCountryTableRows] = useState([]);

  const renderFormField = (fieldName, label, options = {}) => {
    const { required = false, disabled = false, type = "text", placeholder = "", helperText = "", endAdornment = null, onBlur = null } = options;
    return (
      <div>
        <label htmlFor={`outlined-${fieldName}`} className="form-label">{label}{required && <span style={{ color: "red" }}> *</span>}</label>
        <TextField
          {...register(fieldName, {
            required: required ? `${label} is required` : false,
            onBlur: onBlur,
          })}
          id={`outlined-${fieldName}`}
          disabled={disabled}
          type={type}
          style={{ marginBottom: "0", marginLeft: "0" }}
          fullWidth
          placeholder={placeholder}
          variant="outlined"
          helperText={helperText}
          slotProps={endAdornment ? { input: { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> } } : undefined}
        />
        {errors[fieldName] && <Alert severity="error">{errors[fieldName].message}</Alert>}
      </div>
    );
  };

  useEffect(() => {
    if (productId && productId !== null) {
      APIManager.getOneTimeAppDetails(productId).then((json) => {
        setProduct(json);
        setValue("productId", json.product.productId);
        setValue("name", json.product.name);
        setValue("description", json.product.description);
        setValue("trialPeriod", json.trialPrice?.trialPeriod);
        setValue("basePrice", json.oneTimePlan.basePrice.price);

        const priceDataTax = buildPriceDataTax(json);
        setExchangePriceData(priceDataTax);
        createDataGrid(priceDataTax);
        setLoader(false);
      });
    }
    if (submissionDetails?.binary?.packageName) {
      setPackageName(submissionDetails.binary.packageName);
    }
  }, []);

  const backToApplications = () => {
    navigate(-1);
  };

  const backToAppDetails = () => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: { teamId: teamId, sellerId: sellerId },
    });
  };

  const callNewSubmissionId = (submissionId) => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
  };
  const populatePrice = (event) => {
    setLoader(true);
    let basePrice = event.target.value;
    if (!Number.isNaN(Number.parseFloat(basePrice)) && Number.parseFloat(basePrice) != 0) {
      APIManager.getExchangeRates(basePrice, "USD").then((json) => {
        if (json.currencyData && json.currencyData.length > 0) {
          setExchangePriceData(json.currencyData);
          createDataGrid(json.currencyData);
          setLoader(false);
        }
      });
    }
    if (basePrice === "") {
      setExchangePriceData([]);
      setCountryTableRows([]);
    }
    setLoader(false);
  };
  const createDataGrid = (data) => {
    const countryData = createCountryDataRows(data);
    setCountryTableRows(countryData);
  };

  const renderCountryTable = () => {
    if (countryTableRows.length === 0) {
      return null;
    }

    return (
      <div style={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bolder" }}
          gutterBottom
        >
          Country specific price setting{" "}
          <span className="red-star">*</span>
        </Typography>
        <Typography
          variant="caption"
          gutterBottom
          sx={{ display: "block", color: "#757575" }}
        >
          These prices can be manually changed as per how you
          want to change
        </Typography>
        <DataGrid
          rows={countryTableRows}
          columns={COUNTRY_TABLE_COLUMNS}
          disableColumnMenu
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "Items per page:",
            },
          }}
          {...DATA_GRID_CONFIG}
        />
      </div>
    );
  };

  const onSubmit = (data) => {
    setLoader(true);
    let productData = exchangePriceData.filter(
      (item) => item.countryCode !== "USA"
    );
    let planPrices = [];
    productData.forEach((obj) => {
      planPrices.push({
        id: product.oneTimePlan.planPrices.find(
          (item) => item.countryCode === obj.countryCode
        ).id,
        isTaxable: false,
        periodUnit: null,
        price: Number(obj.price).toFixed(2),
        status: "ACTIVE",
        currencyCode: obj.currencyCode,
        countryCode: obj.countryCode,
        country: obj.country,
        taxRateinPercent: obj.taxRateinPercent,
        taxRate: Number(obj.taxRate),
        taxIncluded: false,
        name: data.productId + "_" + obj.countryCode,
        description: data.productId + "_" + obj.countryCode,
      });
    });
    let productToSave = {
      id: productId,
      productId: data.productId,
      name: data.name,
      description: data.description,
      status: "ACTIVE",
      productType: "INAPP",
      resourceType: "NONCONSUMABLE",
      sellerId: sellerId,
      packageId: packageName,
      applicationId: appId,
    };
    let usObject = exchangePriceData.find(
      (item) => item.countryCode === "USA"
    );
    let trialPrice = null;
    if (data.trialPeriod && data.trialPeriod != 0) {
      trialPrice = {
        id: product.trialPrice.id,
        status: "ACTIVE",
        trialPeriod: data.trialPeriod,
        trialPeriodUnit: "DAY",
        trialFlag: true,
        name: data.productId + "_trial",
        description: data.productId + "_trial",
      };
    }
    let basePrice = {
      productId: productId,
      id: product.oneTimePlan.basePrice.id,
      isTaxable: false,
      periodUnit: null,
      price: Number(data.basePrice),
      status: "ACTIVE",
      currencyCode: usObject.currencyCode,
      countryCode: usObject.countryCode,
      country: usObject.country,
      basePrice: true,
      taxRateinPercent: usObject.taxRateinPercent,
      taxRate: usObject.taxRate,
      taxIncluded: false,
      name: data.productId + "_" + usObject.countryCode,
      description: data.productId + "_" + usObject.countryCode,
    };
    let oneTimePlan = {
      id: product.oneTimePlan.id,
      planType: "ONETIME",
      name: data.productId,
      planPrices: planPrices,
      basePrice: basePrice,
    };
    let requestPayload = {
      product: productToSave,
      trialPrice: trialPrice,
      oneTimePlan: oneTimePlan,
    };
    saveProduct(requestPayload);
  };

  const saveProduct = (data) => {
    APIManager.updateInApp(data, productId).then(() => {
      setOpenSnackbar(true);
      setSnackMsg(`Updated successfully`);
      setTimeout(function () {
        navigate(-1);
      }, 3000);
    });
  };

  return (
    <>
      {loader && <LoadingBackdrop open={loader} />}
      <Topheader />
      {
        <Container maxWidth="lg">
          <Grid container>
            <Grid items xs={12} sm={12} md={12} lg={12}></Grid>
            <div className="container" style={submissionDetails.status === "DRAFT" ? { display: "flex", justifyContent: "center", flexDirection: "column", width: "1260px" } : { display: "flex", flexDirection: "row", width: "1260px" }}>
              <div className="side-menu" style={submissionDetails.status === "DRAFT" ? { marginTop: "0px" } : { marginTop: "69px" }}>
                <button className="arrow-back" onClick={backToApplications} type="button" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                  <div><ArrowBackIcon style={{ color: " #717273" }} /></div>
                  <div className="my-apps">My Apps</div>
                </button>
                {submissionDetails && Object.keys(submissionDetails).length > 0 && submissionDetails.status !== "DRAFT" && appData && Object.keys(appData).length > 0 && (
                  <ApplicationDetailsSideMenu appData={appData} teamId={teamId} isMonetized={submissionDetails.isMonetized} callNewSubmissionId={callNewSubmissionId} />
                )}
              </div>
              <div className="submission-container" style={submissionDetails.status === "DRAFT" ? { marginLeft: "0" } : { width: "89%", marginTop: "12px", marginLeft: "70px" }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="in-app-title" style={{ marginLeft: "15%" }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    onClick={backToAppDetails}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackIcon
                      sx={{ mr: 0.5, marginRight: "19px", color: "#b3b3b3" }}
                      fontSize="inherit"
                    />
                    {submissionDetails &&
                      Object.keys(submissionDetails).length > 0 && (
                        <>{submissionDetails.descriptions[0].applicationName}</>
                      )}
                  </Link>
                  <Link
                    underline="hover"
                    onClick={backToApplications}
                    sx={{
                      fontWeight: "500",
                      color: "#2a2c2d",
                      cursor: "pointer",
                    }}
                  >
                    In App Products
                  </Link>
                  <Link
                    underline="none"
                    sx={{ fontWeight: "500", color: "#2a2c2d" }}
                  >
                    Edit Product
                  </Link>
                </Breadcrumbs>
                <Grid container spacing={2}>
                  <Grid size={9}>
                    <h2>Product Details</h2>
                  </Grid>
                  <Grid size={3}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        sx={(theme) => ({
                          backgroundColor: "#e8e8e8",
                          color: "black",
                          textTransform: "none",
                        })}
                        onClick={backToApplications}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        sx={{ textTransform: "none" }}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container">
                <Grid container spacing={2}>
                  <Grid size={4}>Basic Information</Grid>
                  <Grid size={4}>
                    {renderFormField("productId", "Product ID", { required: true, disabled: true, placeholder: "Product ID" })}
                  </Grid>
                  <Grid size={4}>
                    {renderFormField("name", "Name", { required: true, placeholder: "Name" })}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4}></Grid>
                  <Grid size={8}>
                    {renderFormField("description", "Description", { required: true, placeholder: "Description" })}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4}></Grid>
                  <Grid size={3}>
                    <div>
                      <label className="form-label" htmlFor="outlined-textarea">Trial Period</label>
                      <TextField {...register("trialPeriod")} id="outlined-textarea" rows={4} style={{ marginBottom: "0", marginLeft: "0" }} fullWidth placeholder="Trial Period" variant="outlined" />
                    </div>
                  </Grid>
                  <Grid size={1.5}>
                    <div>
                      <Select fullWidth value="" displayEmpty id="trialPeriodUnit" aria-label="Trial period unit" inputProps={{ "aria-label": "Trial period unit" }}>
                        <MenuItem value="">Days</MenuItem>
                      </Select>
                    </div>
                  </Grid>
                </Grid>
              </div>
              <div className="products-container-wide">
                <Divider flexItem />
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}>Recurrence & Price</Grid>
                  <Grid size={8.5}>
                    <div className="monetize-wrapper">
                      <div className="wrapper-diver">
                        <p className="monetize-text" style={{ fontWeight: "bold" }}>
                          Enable Recurrence <span style={{ color: "red" }}>*</span><br />
                          <span className="monetize-text-small" style={{ marginLeft: "0" }}>
                            <b>Note: </b><span style={{ color: "#757575" }}>This setting cannot be undone once this product is published</span>
                          </span>
                        </p>
                        <div><Switch {...register("enableRecurrence", {})} disabled /></div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={4}>
                    <div style={{marginTop: "10%"}}>
                      {renderFormField("basePrice", "Base Price", { required: true, type: "number", endAdornment: "USD", helperText: "Base price doesn't include tax", onBlur: populatePrice })}
                    </div>
                  </Grid>
                </Grid>
                <br />
                <Grid container spacing={2}>
                  <Grid size={3.5}></Grid>
                  <Grid size={8.5}>
                    {renderCountryTable()}
                  </Grid>
                </Grid>
              </div>
            </form>
              </div>
            </div>
          </Grid>
        </Container>
      }
      {errorsApp?.errorMessage && errorsApp?.errorType && <ApplicationDetailsErrorDialog />}
      {(appData.loading || loading) && <LoadingBackdrop open={loading} />}
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} message={snackMsg} />
    </>
  );
}

export default EditOneTimeProduct;


